import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "workspace-intervenants-tab" }
const _hoisted_2 = {
  key: 0,
  class: "workspace-toolbar"
}
const _hoisted_3 = { class: "search-box" }
const _hoisted_4 = ["placeholder"]
const _hoisted_5 = {
  key: 0,
  class: "filter-panel"
}
const _hoisted_6 = { class: "filter-panel-header" }
const _hoisted_7 = { class: "filter-grid" }
const _hoisted_8 = { class: "filter-group" }
const _hoisted_9 = { value: null }
const _hoisted_10 = { value: "ARCHITECT" }
const _hoisted_11 = { value: "CONTRACTOR" }
const _hoisted_12 = { value: "ENGINEER" }
const _hoisted_13 = { value: "CONSULTANT" }
const _hoisted_14 = { value: "SUPPLIER" }
const _hoisted_15 = { value: "OTHER" }
const _hoisted_16 = { class: "filter-group" }
const _hoisted_17 = { value: null }
const _hoisted_18 = { value: "0" }
const _hoisted_19 = { value: "1+" }
const _hoisted_20 = { class: "data-grid" }
const _hoisted_21 = {
  key: 0,
  class: "budget-loading"
}
const _hoisted_22 = {
  key: 1,
  class: "budget-modal-content"
}
const _hoisted_23 = {
  key: 2,
  class: "no-budget-data"
}

import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useNotifications } from '@/composables/useNotifications'
import { usePermissions } from '@/composables/usePermissions'
import PermissionGuard from '@/components/common/PermissionGuard.vue'
import AppDataGrid from '@/components/base/AppDataGrid.vue'
import Modal from '@/components/base/Modal.vue'
import ConfirmModal from '@/components/common/ConfirmModal.vue'
import IntervenantBudgetCard from '../../reports/IntervenantBudgetCard.vue'
import intervenantsService from '@/services/intervenants.service'
import reportService from '@/services/report.service.index'
import { getAvatarColor, getInitials } from '@/utils/uiHelpers'

interface Intervenant {
  id?: number
  projectId?: number
  name: string
  type?: string
  email: string
  phone?: string
  address?: string
  representsFirm?: boolean
  firmName?: string
}

interface Task {
  id?: number
  projectId?: number
  name: string
  amount: number
  status?: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'ON_HOLD'
  intervenantId?: number
  phaseId?: number
}


export default /*@__PURE__*/_defineComponent({
  __name: 'WorkspaceIntervenantsTab',
  props: {
    projectId: {},
    intervenants: {},
    tasks: {},
    showSearch: { type: Boolean },
    showFilters: { type: Boolean },
    filteredIntervenants: {},
    phaseId: {}
  },
  emits: ["intervenantSelected", "intervenantAssigned", "intervenantUnassigned"],
  setup(__props: any, { emit: __emit }) {

// @ts-ignore
const props = __props

const emit = __emit

const { t } = useI18n()
const { notifySuccess, notifyError } = useNotifications()

// Provide default values for optional props
const showSearch = computed(() => props.showSearch !== false) // Default to true
const showFilters = computed(() => props.showFilters === true) // Default to false

// State
const selectedIntervenant = ref<Intervenant | null>(null)
const searchQuery = ref('')
const showAdvancedFilters = ref(false)

// Filter state
const filters = ref({
  type: null as string | null,
  taskCount: null as string | null
})

// Pagination configuration for AppDataGrid
const itemsPerPageOptions = [5, 10, 25, 50, 100]

// Modals
const showBudgetModal = ref(false)
const showDeleteConfirm = ref(false)
const budgetReportData = ref<any | null>(null)
const loadingBudgetReport = ref(false)
const deleteIntervenantId = ref<number | null>(null)
const deleteMessage = ref('')

// Computed - Get intervenants to display (either filtered or all)
const baseIntervenants = computed(() => {
  if (props.filteredIntervenants) {
    return props.filteredIntervenants
  }
  
  // If phaseId is provided, filter intervenants by those who have tasks in that phase
  if (props.phaseId !== undefined) {
    const intervenantIds = new Set(
      props.tasks
        .filter((task: Task) => task.phaseId === props.phaseId)
        .map((task: Task) => task.intervenantId)
        .filter((id: number | undefined) => id !== undefined && id !== null)
    )
    
    return props.intervenants.filter((intervenant: Intervenant) => 
      intervenantIds.has(intervenant.id!)
    )
  }
  
  return props.intervenants
})

// Computed - Apply search and advanced filters
const searchAndFilteredIntervenants = computed(() => {
  let result = [...baseIntervenants.value]

  // Apply search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter((intervenant: Intervenant) =>
      intervenant.name?.toLowerCase().includes(query) ||
      intervenant.email?.toLowerCase().includes(query) ||
      (intervenant.phone && intervenant.phone.includes(query))
    )
  }

  // Apply advanced filters (only if showFilters is true)
  if (showFilters.value) {
    // Type filter
    if (filters.value.type) {
      result = result.filter((intervenant: Intervenant) => intervenant.type === filters.value.type)
    }

    // Task count filter
    if (filters.value.taskCount === '0') {
      result = result.filter((intervenant: Intervenant) => 
        getIntervenantTaskCount(intervenant.id!) === 0
      )
    } else if (filters.value.taskCount === '1+') {
      result = result.filter((intervenant: Intervenant) => 
        getIntervenantTaskCount(intervenant.id!) > 0
      )
    }
  }

  return result
})

// Computed - Check if any filters are active
const hasActiveFilters = computed(() => {
  return filters.value.type || filters.value.taskCount
})

// Methods
const selectIntervenant = (intervenant: Intervenant) => {
  selectedIntervenant.value = intervenant
  emit('intervenantSelected', intervenant)
}

const getIntervenantTaskCount = (intervenantId: number) => {
  let taskFilter = props.tasks.filter((t: Task) => t.intervenantId === intervenantId)
  
  // If phaseId is provided, only count tasks for that phase
  if (props.phaseId !== undefined) {
    taskFilter = taskFilter.filter((t: Task) => t.phaseId === props.phaseId)
  }
  
  return taskFilter.length
}

// Budget Report
const viewIntervenantBudget = async (intervenant: Intervenant) => {
  try {
    loadingBudgetReport.value = true
    showBudgetModal.value = true
    
    // Fetch budget report for the project
    const report = await reportService.getBudgetConsumptionReport(props.projectId)
    
    // Filter to show only the selected intervenant's budget report
    budgetReportData.value = {
      intervenantBudgetReports: report.intervenantBudgetReports?.filter(
        (item: any) => item.intervenantId === intervenant.id
      ) || []
    }
  } catch (error) {
    console.error('Error fetching budget report:', error)
    notifyError(t('intervenants.errors.failedToLoad'))
    showBudgetModal.value = false
  } finally {
    loadingBudgetReport.value = false
  }
}

const closeBudgetModal = () => {
  showBudgetModal.value = false
  budgetReportData.value = null
}

// Edit Intervenant
const editIntervenant = (intervenant: Intervenant) => {
  // Emit to parent to handle editing
  emit('intervenantSelected', intervenant)
}

// Delete Intervenant
const deleteIntervenant = (id: number) => {
  const intervenant = props.intervenants.find((i: Intervenant) => i.id === id)
  deleteIntervenantId.value = id
  deleteMessage.value = intervenant?.name || ''
  showDeleteConfirm.value = true
}

const confirmDelete = async () => {
  if (deleteIntervenantId.value === null) return
  
  try {
    await intervenantsService.deleteIntervenant(
      props.projectId.toString(), 
      deleteIntervenantId.value.toString()
    )
    notifySuccess(t('workspace.success.intervenantDeleted'))
    emit('intervenantUnassigned') // Refresh data
  } catch (error) {
    notifyError(t('workspace.errors.intervenantDeleteFailed'))
  } finally {
    cancelDelete()
  }
}

const cancelDelete = () => {
  showDeleteConfirm.value = false
  deleteIntervenantId.value = null
  deleteMessage.value = ''
}

const clearFilters = () => {
  filters.value = {
    type: null,
    taskCount: null
  }
}

// Grid Configuration
const { hasPermissionForProject, PermissionType } = usePermissions()

// Custom cell renderers for intervenants grid
const intervenantCellRenderers: Record<string, (params: any) => string> = {
  name: (params: any) => {
    const bgColor = getAvatarColor(params.data.nameRaw)
    const initials = getInitials(params.data.nameRaw)
    const firmBadge = params.data.representsFirm ? `
      <span style="
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        font-size: 14px;
        background-color: #dbeafe;
        border-radius: 6px;
        color: #1e40af;
        margin-left: 8px;
      " title="${t('intervenants.firmRepresentative')}">🏢</span>
    ` : ''
    return `
      <div style="display: flex; align-items: center; gap: 12px;">
        <div style="
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background-color: ${bgColor};
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 12px;
          flex-shrink: 0;
        ">${initials}</div>
        <strong style="font-weight: 600; color: #1f2937;">${params.value}</strong>
        ${firmBadge}
      </div>
    `
  },
  
  type: (params: any) => {
    if (!params.value) return '-'
    return `
      <span style="
        padding: 4px 12px;
        border-radius: 12px;
        font-size: 12px;
        font-weight: 600;
        background-color: #dbeafe;
        color: #1e40af;
        display: inline-block;
      ">${params.value}</span>
    `
  },
  
  email: (params: any) => {
    if (!params.value) return '-'
    return `<span style="color: #374151;">${params.value}</span>`
  },
  
  phone: (params: any) => {
    if (!params.value) return '-'
    return `<span style="color: #374151;">${params.value}</span>`
  },
  
  tasks: (params: any) => {
    const count = params.value || 0
    return `
      <span style="
        font-weight: 600;
        color: #374151;
        font-size: 14px;
      ">${count}</span>
    `
  }
}

// Prepare intervenant data for grid
const intervenantGridData = computed(() => {
  return searchAndFilteredIntervenants.value.map((intervenant: Intervenant) => ({
    id: intervenant.id,
    name: intervenant.name,
    nameRaw: intervenant.name,
    type: intervenant.type ? t(`intervenants.types.${intervenant.type}`) : '-',
    typeRaw: intervenant.type,
    email: intervenant.email,
    phone: intervenant.phone || '-',
    tasks: getIntervenantTaskCount(intervenant.id!),
    representsFirm: intervenant.representsFirm,
    _originalData: intervenant
  }))
})

// Column definitions for intervenants grid
const intervenantColumnDefs = computed(() => [
  {
    field: 'name',
    headerName: t('workspace.intervenants.name'),
    cellRenderer: intervenantCellRenderers.name,
    minWidth: 200,
    flex: 1,
    sortable: true,
    wrapText: false,
    autoHeight: false
  },
  {
    field: 'type',
    headerName: t('workspace.intervenants.type'),
    cellRenderer: intervenantCellRenderers.type,
    width: 180,
    sortable: true,
    wrapText: false,
    autoHeight: false
  },
  {
    field: 'email',
    headerName: t('workspace.intervenants.email'),
    cellRenderer: intervenantCellRenderers.email,
    width: 220,
    sortable: true,
    wrapText: false,
    autoHeight: false
  },
  {
    field: 'phone',
    headerName: t('workspace.intervenants.phone'),
    cellRenderer: intervenantCellRenderers.phone,
    width: 150,
    sortable: true,
    wrapText: false,
    autoHeight: false
  },
  {
    field: 'tasks',
    headerName: t('workspace.intervenants.tasks'),
    cellRenderer: intervenantCellRenderers.tasks,
    width: 120,
    sortable: true,
    wrapText: false,
    autoHeight: false
  }
])

// Action definitions for intervenants grid (will be filtered per row based on condition)
const intervenantActions = computed(() => {
  const actions = []
  
  // View Budget Report button
  if (hasPermissionForProject(PermissionType.VIEW_REPORTS, props.projectId).value) {
    actions.push({
      name: 'viewBudget',
      label: t('intervenants.viewBudgetReport'),
      icon: 'fas fa-chart-bar',
      class: 'view-btn',
      handler: (data: any) => viewIntervenantBudget(data._originalData)
    })
  }
  
  // Edit button (hidden for firm representatives)
  if (hasPermissionForProject(PermissionType.UPDATE_INTERVENANT, props.projectId).value) {
    actions.push({
      name: 'edit',
      label: t('common.edit'),
      icon: 'fas fa-edit',
      class: 'edit-btn',
      handler: (data: any) => editIntervenant(data._originalData),
      condition: (data: any) => !data.representsFirm
    })
  }
  
  // Delete button (hidden for firm representatives)
  if (hasPermissionForProject(PermissionType.DELETE_INTERVENANT, props.projectId).value) {
    actions.push({
      name: 'delete',
      label: t('common.delete'),
      icon: 'fas fa-trash',
      class: 'delete-btn',
      handler: (data: any) => deleteIntervenant(data.id),
      condition: (data: any) => !data.representsFirm
    })
  }
  
  return actions
})

// Handle intervenant grid actions
const handleIntervenantAction = (action: any, data: any) => {
  action.handler(data)
}

// Handle intervenant row clicks
const handleIntervenantRowClick = (event: any) => {
  selectIntervenant(event.data._originalData)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (showSearch.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _cache[3] || (_cache[3] = _createElementVNode("span", { class: "search-icon" }, "🔍", -1)),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
              type: "text",
              placeholder: _ctx.$t('workspace.search'),
              class: "search-input"
            }, null, 8, _hoisted_4), [
              [_vModelText, searchQuery.value]
            ])
          ])
        ]))
      : _createCommentVNode("", true),
    _createVNode(_Transition, { name: "slide-down" }, {
      default: _withCtx(() => [
        (showFilters.value && showAdvancedFilters.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, [
                _createElementVNode("h3", null, _toDisplayString(_ctx.$t('workspace.filters.title')), 1),
                (hasActiveFilters.value)
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 0,
                      class: "btn-text",
                      onClick: clearFilters
                    }, _toDisplayString(_ctx.$t('workspace.filters.clearAll')), 1))
                  : _createCommentVNode("", true)
              ]),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("div", _hoisted_8, [
                  _createElementVNode("label", null, _toDisplayString(_ctx.$t('workspace.filters.type')), 1),
                  _withDirectives(_createElementVNode("select", {
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((filters.value.type) = $event)),
                    class: "filter-select"
                  }, [
                    _createElementVNode("option", _hoisted_9, _toDisplayString(_ctx.$t('workspace.filters.allTypes')), 1),
                    _createElementVNode("option", _hoisted_10, _toDisplayString(_ctx.$t('intervenants.types.ARCHITECT')), 1),
                    _createElementVNode("option", _hoisted_11, _toDisplayString(_ctx.$t('intervenants.types.CONTRACTOR')), 1),
                    _createElementVNode("option", _hoisted_12, _toDisplayString(_ctx.$t('intervenants.types.ENGINEER')), 1),
                    _createElementVNode("option", _hoisted_13, _toDisplayString(_ctx.$t('intervenants.types.CONSULTANT')), 1),
                    _createElementVNode("option", _hoisted_14, _toDisplayString(_ctx.$t('intervenants.types.SUPPLIER')), 1),
                    _createElementVNode("option", _hoisted_15, _toDisplayString(_ctx.$t('intervenants.types.OTHER')), 1)
                  ], 512), [
                    [_vModelSelect, filters.value.type]
                  ])
                ]),
                _createElementVNode("div", _hoisted_16, [
                  _createElementVNode("label", null, _toDisplayString(_ctx.$t('workspace.filters.taskCount')), 1),
                  _withDirectives(_createElementVNode("select", {
                    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((filters.value.taskCount) = $event)),
                    class: "filter-select"
                  }, [
                    _createElementVNode("option", _hoisted_17, _toDisplayString(_ctx.$t('workspace.filters.allTaskCounts')), 1),
                    _createElementVNode("option", _hoisted_18, _toDisplayString(_ctx.$t('workspace.filters.noTasks')), 1),
                    _createElementVNode("option", _hoisted_19, _toDisplayString(_ctx.$t('workspace.filters.hasTasks')), 1)
                  ], 512), [
                    [_vModelSelect, filters.value.taskCount]
                  ])
                ])
              ])
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }),
    _createElementVNode("div", _hoisted_20, [
      _createVNode(AppDataGrid, {
        "row-data": intervenantGridData.value,
        "column-defs": intervenantColumnDefs.value,
        actions: intervenantActions.value,
        pagination: true,
        "page-size": 10,
        "page-size-options": itemsPerPageOptions,
        "suppress-pagination-panel": false,
        "show-search": false,
        "grid-class": "intervenants-grid",
        "grid-style": "width: 100%;",
        "dom-layout": "autoHeight",
        "default-col-def": { wrapText: false, autoHeight: false },
        onRowClicked: handleIntervenantRowClick,
        onActionClicked: handleIntervenantAction
      }, null, 8, ["row-data", "column-defs", "actions"])
    ]),
    (showBudgetModal.value)
      ? (_openBlock(), _createBlock(Modal, {
          key: 1,
          show: showBudgetModal.value,
          onClose: closeBudgetModal,
          size: "large",
          title: _ctx.$t('intervenants.budgetReport')
        }, {
          default: _withCtx(() => [
            (loadingBudgetReport.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                  _cache[4] || (_cache[4] = _createElementVNode("div", { class: "loading-spinner" }, null, -1)),
                  _createElementVNode("p", null, _toDisplayString(_ctx.$t('common.loading')), 1)
                ]))
              : (budgetReportData.value?.intervenantBudgetReports?.length)
                ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(budgetReportData.value.intervenantBudgetReports, (intervenant) => {
                      return (_openBlock(), _createBlock(IntervenantBudgetCard, {
                        key: intervenant.intervenantId,
                        intervenant: intervenant
                      }, null, 8, ["intervenant"]))
                    }), 128))
                  ]))
                : (_openBlock(), _createElementBlock("div", _hoisted_23, [
                    _createElementVNode("p", null, _toDisplayString(_ctx.$t('intervenants.noBudgetData')), 1)
                  ]))
          ]),
          _: 1
        }, 8, ["show", "title"]))
      : _createCommentVNode("", true),
    (showDeleteConfirm.value)
      ? (_openBlock(), _createBlock(ConfirmModal, {
          key: 2,
          isVisible: showDeleteConfirm.value,
          title: _ctx.$t('intervenants.deleteIntervenant'),
          message: _ctx.$t('workspace.confirmDelete.intervenant'),
          details: deleteMessage.value,
          "confirm-text": _ctx.$t('common.delete'),
          "cancel-text": _ctx.$t('common.cancel'),
          type: "danger",
          onConfirm: confirmDelete,
          onCancel: cancelDelete
        }, null, 8, ["isVisible", "title", "message", "details", "confirm-text", "cancel-text"]))
      : _createCommentVNode("", true)
  ]))
}
}

})